/**
 * Which OTA release channel THIS INSTALL is on (Cards 10/11/13).
 *
 * WHY THIS EXISTS. Two things need the channel, and until now only one of them had it right:
 *
 *   - the Capgo plugin, which downloads JS bundles. Its channel is `defaultChannel` in the
 *     `capacitor.config.json` compiled into the binary at `cap sync` time, from
 *     `CAP_UPDATE_CHANNEL`.
 *   - the in-app update banner (`useUpdateAvailable`), which asks
 *     `GET /api/releases/manifest?channel=…` whether a newer NATIVE build exists. It hardcoded
 *     `'beta'`, so a binary pinned to `production` still asked the beta manifest.
 *
 * On iOS that is invisible — the beta manifest publishes no `ios-ipa` artifact and the banner
 * needs one for the running platform — so the cost is telemetry filed under the wrong channel. On
 * Android it is visible and is why this matters: a Play install would be offered the beta APK from
 * downloads.gotradeloop.com.
 *
 * ── WHY A BUILD-TIME CONSTANT IS NOT ENOUGH ──────────────────────────────────────────────────
 *
 * The obvious fix is to bake the channel into the bundle from the same env var the native config
 * uses. That is correct for a native binary and WRONG FOR THE NORMAL RELEASE PATH. Every release
 * here goes out to `beta` first and is then PROMOTED: `mobile-ota.yml`'s promote operation copies
 * the bundle from `beta` to `production` byte for byte — deliberately, because the whole point is
 * that production runs the exact artifact beta tested. A baked constant survives that copy, so
 * every promoted bundle would tell a production device it is on `beta`. Since promotion is the
 * steady state, a baked constant would be wrong almost all of the time — right on a fresh store
 * install, wrong from its first OTA update onward.
 *
 * The channel is therefore a property of the INSTALL, not of the bundle, and has to be read at
 * runtime.
 *
 * ── HOW IT IS READ, AND WHY THAT IS SAFE ─────────────────────────────────────────────────────
 *
 * `CapacitorUpdater.getChannel()` is the only bridge to the native value: the plugin exposes no
 * local getter and Capacitor does not surface its config to JS. It is a network call — the plugin
 * PUTs `defaultChannel` (the binary's own value) to `channelUrl`, our `/api/releases/channel`.
 *
 * Its documented side effect is the thing to respect: on a successful response the plugin calls
 * `persistDefaultChannelFromResponse()` and keeps the returned channel across restarts. A wrong
 * answer does not mislead one request, it MOVES the install onto another channel permanently. That
 * route was therefore hardened alongside this file: it now echoes only a channel the device itself
 * named, and answers 400 `channel_not_found` otherwise — which the plugin handles by falling back
 * to the binary's compiled `defaultChannel` and persisting nothing. Either way the value that
 * comes back is the binary's own.
 *
 * ── FALLBACK ORDER, AND WHY IT IS THIS ORDER ─────────────────────────────────────────────────
 *
 *   1. The cached result of a previous successful read. Cached because a promoted bundle inherits
 *      the WebView's localStorage from the bundle before it, so an install that has resolved its
 *      channel once stays right forever after — including offline, and including across the OTA
 *      swap that would otherwise lose the answer.
 *   2. A live `getChannel()`, which refreshes the cache.
 *   3. `BUILD_UPDATE_CHANNEL`, the value compiled into this bundle. Correct on a native binary's
 *      own built-in bundle, which is exactly the case where nothing is cached yet — a first launch
 *      after install. Wrong only on a promoted bundle that has never once been online, where the
 *      cost is a banner that asks the wrong manifest until the first successful read.
 */

import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { RELEASE_CHANNELS, type ReleaseChannel } from '@/types/release';
import { isNative } from '@/utils/platform';

declare const __OTA_CHANNEL__: string;

const CACHE_KEY = 'tradeloop.release.resolvedChannel';

function asChannel(value: unknown): ReleaseChannel | null {
  return typeof value === 'string' && (RELEASE_CHANNELS as readonly string[]).includes(value)
    ? (value as ReleaseChannel)
    : null;
}

/**
 * The channel compiled into THIS BUNDLE by `vite.config.ts` (`__OTA_CHANNEL__`, from
 * `CAP_UPDATE_CHANNEL`). A last-resort fallback only — read the header for why it cannot be the
 * answer on its own.
 */
const BUILD_UPDATE_CHANNEL: ReleaseChannel =
  asChannel(typeof __OTA_CHANNEL__ === 'string' ? __OTA_CHANNEL__ : null) ?? 'beta';

/** localStorage throws in private-mode webviews; resolving a channel must never break the app. */
function readCache(): ReleaseChannel | null {
  try {
    return asChannel(window.localStorage.getItem(CACHE_KEY));
  } catch {
    return null;
  }
}

function writeCache(channel: ReleaseChannel): void {
  try {
    window.localStorage.setItem(CACHE_KEY, channel);
  } catch {
    /* ignore */
  }
}

/** Best answer available without waiting on the network — the cache, else what we were built as. */
function currentUpdateChannel(): ReleaseChannel {
  return readCache() ?? BUILD_UPDATE_CHANNEL;
}

let inFlight: Promise<ReleaseChannel> | null = null;

/**
 * The channel this install is on. Memoised for the life of the page: the value cannot change
 * under us — `POST /api/releases/channel` refuses self-service switching precisely so an installed
 * app can never move itself onto an unreviewed build.
 */
export function resolveUpdateChannel(): Promise<ReleaseChannel> {
  if (inFlight) return inFlight;

  inFlight = (async (): Promise<ReleaseChannel> => {
    // On the web there is no native config and no OTA at all; the banner does not run there
    // either (`useUpdateAvailable` guards on `isNative`).
    if (!isNative) return currentUpdateChannel();

    try {
      const result = await CapacitorUpdater.getChannel();
      const channel = asChannel(result?.channel);
      if (channel) {
        writeCache(channel);
        return channel;
      }
    } catch {
      // Offline, rate-limited, or an older plugin build. Fall through — a channel we cannot read
      // is not an error the user should ever see.
    }

    return currentUpdateChannel();
  })();

  return inFlight;
}
